import type {ReactElement} from 'react';
import React from 'react';
import {BaseContextualMenuGridPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-contextual-menu-grid-panel/base-contextual-menu-grid-panel';
import type {ContextualMenuGridPanelItem} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-contextual-menu-grid-panel/base-contextual-menu-grid-panel.types';
import {Suboption} from '@Components/contextual-menu/components/suboption';
import {Text, TextSize} from '@Components/text';
import type {IconInfo} from '@Components/icon-group/icon-group';
import {IconGroup} from '@Components/icon-group/icon-group';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {AnimationSpeed, AnimationType, PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {SliderTextOptions} from '@Components/slider-text-options';
import styles from './animation-styles-panel.module.scss';
import {Thumbnail} from '@Components/thumbnail';
import type {ThumbnailData} from '@Components/thumbnail/thumbnail.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {ThumbnailItem} from '@Components/thumbnail-item';
import {PreviewType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {ContextualMenu} from '@Components/contextual-menu';

export interface AnimationStylesPanelProps {
  selectedAnimation: AnimationType;
  onAnimationTypeModified: (id: AnimationType) => void;
  onAnimationSpeedModified: (undoable: boolean, replayPosterOnChange: boolean, speed: AnimationSpeed) => void;
  onAnimationDirectionModified: (direction: SlideType | PositionType) => void;
  speed: AnimationSpeed;
  direction: SlideType | PositionType;
  animations: AnimationType[];
  showInGrid: boolean;
  previewType?: PreviewType;
}

export function AnimationStylesPanel({selectedAnimation = AnimationType.NONE, previewType = PreviewType.DESIGN, ...props}: AnimationStylesPanelProps): ReactElement | null {
  const getThumbnailData = (animation: AnimationType): ThumbnailData => {
    if (animation === AnimationType.NONE) {
      return {
        type: ThumbnailType.IMAGE,
        showSkeletonLoading: true,
        imageSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/none.png`),
      };
    }
    if (animation === selectedAnimation) {
      return {
        type: ThumbnailType.IMAGE,
        showSkeletonLoading: true,
        imageSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/${animation}.png?ts=1744006311`),
      };
    }
    return {
      type: ThumbnailType.VIDEO,
      showSkeletonLoading: true,
      imageSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/${animation}.png?ts=1744006311`),
      videoSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/${animation}.mp4?ts=1745823940`),
    };
  };

  const getThumbnail = (animation: AnimationType): ReactElement => {
    return <Thumbnail key={animation} className={props.showInGrid ? styles.largeImg : styles.smallImg} data={getThumbnailData(animation)} />;
  };

  const getThumbnailItem = (animation: AnimationType): ReactElement => {
    return (
      <ThumbnailItem
        thumbnailContainerClassName={'radius-8'}
        hasBorder={false}
        isSelected={selectedAnimation === animation}
        key={animation}
        thumbnail={getThumbnail(animation)}
        onClick={(): void => {
          if (animation !== selectedAnimation) {
            props.onAnimationTypeModified(animation);
          }
        }}
        textClassName={'content-sub-text'}
        text={getNameForAnimationType(animation)}
      />
    );
  };

  const getNameForAnimationType = (animation: AnimationType): string => {
    switch (animation) {
      case AnimationType.BLOCK:
        return window.i18next.t('pmwjs_block');
      case AnimationType.BOUNCE:
        return window.i18next.t('pmwjs_bounce');
      case AnimationType.FADE:
        return window.i18next.t('pmwjs_fade');
      case AnimationType.JELLO:
        return window.i18next.t('pmwjs_jello');
      case AnimationType.PAN:
        return window.i18next.t('pmwjs_pan');
      case AnimationType.POP:
        return window.i18next.t('pmwjs_pop');
      case AnimationType.ROTATE:
        return window.i18next.t('pmwjs_rotate');
      case AnimationType.SCALE:
        return window.i18next.t('pmwjs_scale');
      case AnimationType.SLIDE:
        return window.i18next.t('pmwjs_slide');
      case AnimationType.TUMBLE:
        return window.i18next.t('pmwjs_tumble');
      case AnimationType.WIPE:
        return window.i18next.t('pmwjs_wipe');
      case AnimationType.PEEK:
        return window.i18next.t('pmwjs_peek');
      case AnimationType.ZOOM:
        return window.i18next.t('pmwjs_zoom');
      case AnimationType.NONE:
      default:
        return window.i18next.t('pmwjs_none');
    }
  };
  const getItems = (): ContextualMenuGridPanelItem[] => {
    return props.animations.map((animation) => {
      return {
        id: animation,
        name: getNameForAnimationType(animation),
        thumb: getThumbnail(animation),
      };
    });
  };

  const getTextForSpeed = (speed: AnimationSpeed): string => {
    switch (speed) {
      case AnimationSpeed.HIGHER:
        return window.i18next.t('pmwjs_higher');
      case AnimationSpeed.HIGH:
        return window.i18next.t('pmwjs_high');
      case AnimationSpeed.LOWER:
        return window.i18next.t('pmwjs_lower');
      case AnimationSpeed.LOW:
        return window.i18next.t('pmwjs_low');
      case AnimationSpeed.MEDIUM:
      default:
        return window.i18next.t('pmwjs_medium');
    }
  };

  const getSliderOption = (): ReactElement => {
    return (
      <SliderTextOptions
        data={[AnimationSpeed.LOWER, AnimationSpeed.LOW, AnimationSpeed.MEDIUM, AnimationSpeed.HIGH, AnimationSpeed.HIGHER]}
        label={window.i18next.t('pmwjs_speed')}
        className={props.showInGrid ? '' : 'spacing-p-b-3'}
        value={getTextForSpeed(props.speed)}
        selectedId={props.speed}
        onChange={(speed: string): void => {
          if (speed !== props.speed.toString()) {
            props.onAnimationSpeedModified(false, false, speed as AnimationSpeed);
          }
        }}
        onChangeEnded={(speed: string): void => {
          if (speed !== props.speed.toString()) {
            props.onAnimationSpeedModified(true, true, speed as AnimationSpeed);
          }
        }}
      />
    );
  };

  const getIcons = (): IconInfo[] => {
    switch (selectedAnimation) {
      case AnimationType.ROTATE:
        return [
          {
            id: PositionType.LEFT,
            name: 'icon-rotate-cw',
            isSelected: props.direction === PositionType.LEFT,
          },
          {
            id: PositionType.RIGHT,
            name: 'icon-rotate-acw',
            isSelected: props.direction === PositionType.RIGHT,
          },
        ];
      case AnimationType.SCALE:
      case AnimationType.ZOOM:
        return [
          {
            id: PositionType.LEFT,
            name: 'icon-scale-out',
            isSelected: props.direction === PositionType.LEFT,
          },
          {
            id: PositionType.RIGHT,
            name: 'icon-scale-in',
            isSelected: props.direction === PositionType.RIGHT,
          },
        ];
      default:
        return [
          {
            id: PositionType.RIGHT,
            name: 'icon-arrow-right',
            isSelected: props.direction === PositionType.RIGHT,
          },
          {
            id: PositionType.LEFT,
            name: 'icon-arrow-left',
            isSelected: props.direction === PositionType.LEFT,
          },
          {
            id: PositionType.TOP,
            name: 'icon-arrow-top',
            isSelected: props.direction === PositionType.TOP,
          },
          {
            id: PositionType.BOTTOM,
            name: 'icon-arrow-bottom',
            isSelected: props.direction === PositionType.BOTTOM,
          },
        ];
    }
  };

  const getDirectionOptions = (): ReactElement | undefined => {
    if (!shouldShowDirection()) {
      return;
    }
    return (
      <div className={`flex-row-justify-between flex-row-align-center ${props.showInGrid ? '' : 'spacing-p-b-3'}`}>
        <Text val={window.i18next.t('pmwjs_direction')} size={TextSize.XSMALL} />
        <IconGroup
          size={IconSize.SIZE_ICON_16}
          onIconSelect={(id: string): void => {
            if (id !== props.direction.toString()) {
              props.onAnimationDirectionModified(id as PositionType);
            }
          }}
          icons={getIcons()}
        />
      </div>
    );
  };

  const shouldShowSubOptions = (): boolean => {
    return ![AnimationType.NONE].includes(selectedAnimation);
  };

  const shouldShowDirection = (): boolean => {
    return ![AnimationType.JELLO, AnimationType.POP, AnimationType.FADE, AnimationType.TUMBLE].includes(selectedAnimation);
  };

  const getSubOptionForGrid = (): ReactElement | undefined => {
    if (!shouldShowSubOptions()) {
      return;
    }
    return (
      <Suboption
        className={'spacing-m-b-5'}
        children={
          <>
            {getSliderOption()}
            {getDirectionOptions()}
          </>
        }
      />
    );
  };

  const getSubOptions = (): ReactElement | null => {
    if (!shouldShowSubOptions()) {
      return null;
    }
    return (
      <>
        {getSliderOption()}
        {getDirectionOptions()}
      </>
    );
  };

  if (props.showInGrid) {
    return (
      <div className={'flex-center'}>
        <BaseContextualMenuGridPanel
          hasBorder={false}
          items={getItems()}
          hasCustomDimensions={true}
          selectedId={selectedAnimation}
          containerClass={styles.gridContainer}
          hasSubOptions={true}
          subOptionToShow={getSubOptionForGrid()}
          showNameWithThumb={true}
          onSelected={(id: string) => {
            if (id !== selectedAnimation.toString()) {
              props.onAnimationTypeModified(id as AnimationType);
            }
          }}
        />
      </div>
    );
  }

  return (
    <ContextualMenu id="animation-styles-mobile-panel" hasMobileModalPadding hasMobileMorphingProperties>
      {getSubOptions()}
      <div>
        <div className={`flexbox ${styles.items}`}>
          {props.animations.map((animation) => {
            return getThumbnailItem(animation);
          })}
        </div>
      </div>
    </ContextualMenu>
  );
}
